/**
 * Particle System — hard glow textures with object pool.
 * Rich multi-layer explosions + shockwave rings + chiclet gems.
 *
 * Core infrastructure: pool management, textures, update loop, destroy.
 * Spawn methods delegate to standalone functions in sibling modules.
 */

import {
  Container,
  Graphics,
  Sprite,
  RenderTexture,
  BlurFilter,
  type Renderer as PixiRendererType,
} from 'pixi.js';
import { Z } from '../../constants';
import type { Particle, Shockwave, ImpactLine, BeamLine, PendingBlast, ParticleCore } from './types';
import { POOL_SIZE, configParticle } from './types';

// Import spawn functions from modules
import { spawnExplosion as _spawnExplosion, spawnTerrainDebris as _spawnTerrainDebris, spawnRichExplosion as _spawnRichExplosion, spawnHitCross as _spawnHitCross, spawnStructuralDebris as _spawnStructuralDebris } from './explosions';
import { spawnVoidDeathExplosion as _spawnVoidDeathExplosion, spawnVoidProjectileConsume as _spawnVoidProjectileConsume, spawnVoidForeshadow as _spawnVoidForeshadow, spawnVoidLaunchBurst as _spawnVoidLaunchBurst } from './void';
import { spawnDIEntry as _spawnDIEntry, spawnDIAmbient as _spawnDIAmbient, spawnDIInputActive as _spawnDIInputActive, spawnDIResolve as _spawnDIResolve } from './di';
import { spawnBounceImpact as _spawnBounceImpact, spawnWallBounce as _spawnWallBounce, spawnCeilingBounce as _spawnCeilingBounce, spawnDirectionChange as _spawnDirectionChange, spawnClimbDust as _spawnClimbDust, spawnLaunchTrail as _spawnLaunchTrail, spawnHopDust as _spawnHopDust, spawnLiftTrail as _spawnLiftTrail, spawnLiftBurst as _spawnLiftBurst, spawnFastFallActivation as _spawnFastFallActivation, spawnFastFallTrail as _spawnFastFallTrail } from './movement';
import { spawnHeavyMuzzleFlash as _spawnHeavyMuzzleFlash, spawnMuzzleFlash as _spawnMuzzleFlash, spawnGrappleLaunch as _spawnGrappleLaunch, spawnGrappleAttach as _spawnGrappleAttach, spawnGrappleArrival as _spawnGrappleArrival, spawnRopeScrape as _spawnRopeScrape, spawnGrappleTrail as _spawnGrappleTrail, spawnDuelistSlash as _spawnDuelistSlash, spawnDuelistArc as _spawnDuelistArc, spawnTipperDetonation as _spawnTipperDetonation, spawnBouncerBankShot as _spawnBouncerBankShot, spawnScatterBuckshot as _spawnScatterBuckshot, spawnVortexVoidTear as _spawnVortexVoidTear, spawnGyroSpinLaunch as _spawnGyroSpinLaunch, spawnDuelistStartup as _spawnDuelistStartup, spawnDuelistDashStart as _spawnDuelistDashStart, spawnDuelistAfterimage as _spawnDuelistAfterimage, spawnDuelistSpeedLines as _spawnDuelistSpeedLines, spawnDuelistBladeTrail as _spawnDuelistBladeTrail, spawnDuelistSlashResolve as _spawnDuelistSlashResolve, spawnCupidMarkApplied as _spawnCupidMarkApplied, spawnCupidHeartbeatHit as _spawnCupidHeartbeatHit, spawnCupidHeartbreak as _spawnCupidHeartbreak, spawnFallingStarTrail as _spawnFallingStarTrail, spawnFallingStarImpact as _spawnFallingStarImpact } from './weapon-fx';
import { spawnTurnIndicator as _spawnTurnIndicator, spawnAimSweepParticles as _spawnAimSweepParticles, spawnKnockbackIndicator as _spawnKnockbackIndicator } from './hud';
import { spawnBurstEntry as _spawnBurstEntry, spawnBurstAmbient as _spawnBurstAmbient, spawnBurstInputActive as _spawnBurstInputActive, spawnBurstResolve as _spawnBurstResolve, spawnBurstTrail as _spawnBurstTrail, spawnControlSnap as _spawnControlSnap, spawnRecoveryBurst as _spawnRecoveryBurst, spawnBraceHeal as _spawnBraceHeal } from './burst';
import { spawnEdgeguardEntry as _spawnEdgeguardEntry, spawnEdgeguardFireLines as _spawnEdgeguardFireLines, spawnEdgeguardEndFlash as _spawnEdgeguardEndFlash } from './edgeguard';
import { spawnDefenseEntry as _spawnDefenseEntry, spawnBrakeExplosion as _spawnBrakeExplosion, spawnBrakeWindowPulse as _spawnBrakeWindowPulse } from './defense';

export class ParticleSystem implements ParticleCore {
  private container: Container;
  pool: Particle[] = [];
  activeCount: number = 0;
  softGlowTexture: RenderTexture;
  hardGlowTexture: RenderTexture;
  clusterTexture: RenderTexture;
  shockwaves: Shockwave[] = [];
  impactLines: ImpactLine[] = [];
  beamLines: BeamLine[] = [];
  private shockwaveGfx: Graphics;
  pendingBlasts: PendingBlast[] = [];

  // Bolt: Reuse friction cache to avoid per-frame allocation & Math.pow re-calc
  private frictionCache = new Map<number, number>();

  constructor(worldContainer: Container, renderer: PixiRendererType) {
    this.container = new Container();
    this.container.zIndex = Z.PARTICLES;
    worldContainer.addChild(this.container);

    // Three textures: hard square, soft circle, multi-gem cluster
    this.hardGlowTexture = this.bakeHardGlowTexture(renderer, 10, 2);
    this.softGlowTexture = this.bakeSoftGlowTexture(renderer, 6, 14);
    this.clusterTexture = this.bakeClusterTexture(renderer);

    // Pre-allocate pool — start with hard texture (most particles use it)
    for (let i = 0; i < POOL_SIZE; i++) {
      const sprite = new Sprite(this.hardGlowTexture);
      sprite.anchor.set(0.5, 0.5);
      sprite.blendMode = 'add';
      sprite.visible = false;
      this.container.addChild(sprite);

      this.pool.push({
        sprite,
        x: 0, y: 0, vx: 0, vy: 0,
        life: 0, maxLife: 1, startScale: 1, startAlpha: 1, stretch: 1,
        gravity: 0, friction: 0.98, rotationSpeed: 0,
        addBlend: true, hard: true, active: false, freezeImmune: false,
      });
    }

    // Shockwave graphics layer — added after pool sprites so it renders on top
    this.shockwaveGfx = new Graphics();
    this.container.addChild(this.shockwaveGfx);
  }

  /** Hard glow: solid opaque square + thin glow halo. */
  private bakeHardGlowTexture(renderer: PixiRendererType, radius: number, haloSize: number): RenderTexture {
    const padding = haloSize * 2;
    const size = (radius + padding) * 2;
    const half = radius;

    const stage = new Container();

    // Layer 1: blurred halo underneath
    const haloGfx = new Graphics();
    haloGfx.rect(size / 2 - half, size / 2 - half, half * 2, half * 2);
    haloGfx.fill({ color: 0xffffff, alpha: 1 });
    haloGfx.filters = [new BlurFilter({ strength: haloSize, quality: 3 })];
    stage.addChild(haloGfx);

    // Layer 2: sharp solid square on top
    const coreGfx = new Graphics();
    coreGfx.rect(size / 2 - half, size / 2 - half, half * 2, half * 2);
    coreGfx.fill({ color: 0xffffff, alpha: 1 });
    stage.addChild(coreGfx);

    const rt = RenderTexture.create({ width: size, height: size });
    renderer.render({ container: stage, target: rt });
    stage.destroy({ children: true });
    return rt;
  }

  /** Soft glow: diffuse blur for smoke and ambient effects */
  private bakeSoftGlowTexture(renderer: PixiRendererType, radius: number, blurAmount: number): RenderTexture {
    const padding = blurAmount * 2;
    const size = (radius + padding) * 2;

    const gfx = new Graphics();
    gfx.circle(size / 2, size / 2, radius);
    gfx.fill({ color: 0xffffff, alpha: 1 });
    gfx.filters = [new BlurFilter({ strength: blurAmount, quality: 4 })];

    const rt = RenderTexture.create({ width: size, height: size });
    renderer.render({ container: gfx, target: rt });
    gfx.destroy();
    return rt;
  }

  /** Multi-gem cluster: 4 small squares arranged in a scattered group (Sakurai billboard trick) */
  private bakeClusterTexture(renderer: PixiRendererType): RenderTexture {
    const size = 32;
    const stage = new Container();

    // 4 small squares at scattered positions — one sprite reads as multiple gems
    const positions = [
      { x: 8, y: 10, s: 5 },
      { x: 20, y: 8, s: 4 },
      { x: 14, y: 20, s: 5 },
      { x: 24, y: 22, s: 3 },
    ];
    for (const pos of positions) {
      const g = new Graphics();
      g.rect(pos.x - pos.s / 2, pos.y - pos.s / 2, pos.s, pos.s);
      g.fill({ color: 0xffffff, alpha: 1 });
      stage.addChild(g);
    }

    // Light blur to soften edges slightly
    stage.filters = [new BlurFilter({ strength: 1, quality: 2 })];

    const rt = RenderTexture.create({ width: size, height: size });
    renderer.render({ container: stage, target: rt });
    stage.destroy({ children: true });
    return rt;
  }

  getInactive(): Particle | null {
    if (this.activeCount >= POOL_SIZE) return null;
    const p = this.pool[this.activeCount];
    this.activeCount++;
    return p;
  }

  spawnParticle(
    x: number, y: number,
    color: number,
    speedMin: number, speedMax: number,
    lifeMin: number, lifeMax: number,
    scaleMin: number, scaleMax: number,
    gravity: number,
    friction: number,
    addBlend: boolean = true,
    hard: boolean = true,
    alpha: number = 1,
    stretch: number = 1,
    spawnOffset: number = 0, // offset along travel direction to reduce center overlap
  ): Particle | null {
    const p = this.getInactive();
    if (!p) return null;

    const angle = Math.random() * Math.PI * 2;
    const speed = speedMin + Math.random() * (speedMax - speedMin);

    // Sakurai: offset spawn positions to avoid wasteful center overlap
    const ox = spawnOffset > 0 ? Math.cos(angle) * Math.random() * spawnOffset : 0;
    const oy = spawnOffset > 0 ? Math.sin(angle) * Math.random() * spawnOffset : 0;

    configParticle(p, this, {
      x: x + ox, y: y + oy,
      vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
      color,
      life: lifeMin + Math.random() * (lifeMax - lifeMin),
      scale: scaleMin + Math.random() * (scaleMax - scaleMin),
      alpha, stretch, gravity, friction, addBlend, hard,
      rotation: stretch > 1 ? angle : undefined,
    });
    return p;
  }

  /** Spawn a particle at a specific angle (for ring bursts) */
  spawnParticleAtAngle(
    x: number, y: number,
    angle: number, speed: number,
    color: number,
    life: number,
    scale: number,
    gravity: number,
    friction: number,
    addBlend: boolean = true,
    hard: boolean = true,
    alpha: number = 1,
    stretch: number = 1,
  ): Particle | null {
    const p = this.getInactive();
    if (!p) return null;

    configParticle(p, this, {
      x, y,
      vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
      color, life, scale,
      alpha, stretch, gravity, friction, addBlend, hard,
      rotation: stretch > 1 ? angle : undefined,
    });
    return p;
  }

  spawnShockwave(
    x: number, y: number,
    color: number,
    maxRadius: number,
    expandSpeed: number,
    lineWidth: number,
    life: number,
  ): void {
    this.shockwaves.push({
      x, y,
      radius: 0,
      maxRadius,
      expandSpeed,
      color,
      lineWidth,
      life,
      maxLife: life,
      freezeImmune: false,
    });
  }

  spawnBeamLine(
    x: number, startY: number, endY: number,
    color: number, width: number, life: number,
  ): void {
    this.beamLines.push({
      x, startY, endY, color, width,
      life, maxLife: life, freezeImmune: true,
    });
  }

  // === Delegation methods — preserve the full public API ===

  /** Legacy explosion — used by death sequence and other non-weapon effects */
  spawnExplosion(x: number, y: number, color: number, count: number): void {
    _spawnExplosion(this, x, y, color, count);
  }

  spawnTerrainDebris(cx: number, cy: number, blastRadius: number, particleScale: number): void {
    _spawnTerrainDebris(this, cx, cy, blastRadius, particleScale);
  }

  spawnRichExplosion(x: number, y: number, color: number, blastRadius: number, scale: number): void {
    _spawnRichExplosion(this, x, y, color, blastRadius, scale);
  }

  spawnHitCross(x: number, y: number, blastRadius: number): void {
    _spawnHitCross(this, x, y, blastRadius);
  }

  spawnStructuralDebris(cx: number, cy: number, cellCount: number): void {
    _spawnStructuralDebris(this, cx, cy, cellCount);
  }

  spawnVoidDeathExplosion(x: number, y: number, playerColor: number, voidY: number, voidColor?: number): void {
    _spawnVoidDeathExplosion(this, x, y, playerColor, voidY, voidColor);
  }

  spawnVoidProjectileConsume(x: number, voidY: number, voidColor?: number): void {
    _spawnVoidProjectileConsume(this, x, voidY, voidColor);
  }

  spawnVoidForeshadow(x: number, voidY: number, voidColor?: number): void {
    _spawnVoidForeshadow(this, x, voidY, voidColor);
  }

  spawnVoidLaunchBurst(x: number, voidY: number, voidColor?: number): void {
    _spawnVoidLaunchBurst(this, x, voidY, voidColor);
  }

  spawnDIEntry(cx: number, cy: number, playerColor: number): void {
    _spawnDIEntry(this, cx, cy, playerColor);
  }

  spawnDIAmbient(cx: number, cy: number, playerColor: number, kbAngle: number): void {
    _spawnDIAmbient(this, cx, cy, playerColor, kbAngle);
  }

  spawnDIInputActive(cx: number, cy: number, inputX: number, inputY: number, playerColor: number): void {
    _spawnDIInputActive(this, cx, cy, inputX, inputY, playerColor);
  }

  spawnDIResolve(cx: number, cy: number, launchVx: number, launchVy: number, playerColor: number): void {
    _spawnDIResolve(this, cx, cy, launchVx, launchVy, playerColor);
  }

  spawnBounceImpact(cx: number, cy: number, playerColor: number, intensity: number): void {
    _spawnBounceImpact(this, cx, cy, playerColor, intensity);
  }

  spawnWallBounce(cx: number, cy: number, inwardDir: number, playerColor: number, intensity: number, isSpike: boolean): void {
    _spawnWallBounce(this, cx, cy, inwardDir, playerColor, intensity, isSpike);
  }

  spawnCeilingBounce(cx: number, cy: number, playerColor: number, intensity: number): void {
    _spawnCeilingBounce(this, cx, cy, playerColor, intensity);
  }

  spawnDirectionChange(cx: number, cy: number, oldDir: number, playerColor: number): void {
    _spawnDirectionChange(this, cx, cy, oldDir, playerColor);
  }

  spawnClimbDust(x: number, feetY: number, climbDir: number, playerColor: number): void {
    _spawnClimbDust(this, x, feetY, climbDir, playerColor);
  }

  spawnLaunchTrail(x: number, y: number, vx: number, vy: number, playerColor: number, speed: number): void {
    _spawnLaunchTrail(this, x, y, vx, vy, playerColor, speed);
  }

  spawnHopDust(cx: number, feetY: number, playerColor: number): void {
    _spawnHopDust(this, cx, feetY, playerColor);
  }

  spawnLiftTrail(x: number, y: number, vx: number, vy: number, playerColor: number): void {
    _spawnLiftTrail(this, x, y, vx, vy, playerColor);
  }

  spawnLiftBurst(x: number, y: number, vx: number, vy: number, playerColor: number): void {
    _spawnLiftBurst(this, x, y, vx, vy, playerColor);
  }

  spawnFastFallActivation(cx: number, cy: number, playerColor: number): void {
    _spawnFastFallActivation(this, cx, cy, playerColor);
  }

  spawnFastFallTrail(x: number, y: number, vy: number, playerColor: number): void {
    _spawnFastFallTrail(this, x, y, vy, playerColor);
  }

  spawnHeavyMuzzleFlash(x: number, y: number, angle: number, color: number): void {
    _spawnHeavyMuzzleFlash(this, x, y, angle, color);
  }

  spawnMuzzleFlash(x: number, y: number, angle: number, color: number): void {
    _spawnMuzzleFlash(this, x, y, angle, color);
  }

  spawnGrappleLaunch(x: number, y: number, angle: number, color: number): void {
    _spawnGrappleLaunch(this, x, y, angle, color);
  }

  spawnGrappleAttach(x: number, y: number, color: number): void {
    _spawnGrappleAttach(this, x, y, color);
  }

  spawnGrappleArrival(x: number, y: number, color: number): void {
    _spawnGrappleArrival(this, x, y, color);
  }

  spawnRopeScrape(x: number, y: number, color: number): void {
    _spawnRopeScrape(this, x, y, color);
  }

  spawnGrappleTrail(x: number, y: number, vx: number, vy: number, color: number): void {
    _spawnGrappleTrail(this, x, y, vx, vy, color);
  }

  spawnDuelistSlash(baseX: number, baseY: number, angle: number, slashRange: number, chargeTier: number): void {
    _spawnDuelistSlash(this, baseX, baseY, angle, slashRange, chargeTier);
  }

  spawnDuelistArc(baseX: number, baseY: number, arcPoints: { x: number; y: number; t: number }[], dirX: number): void {
    _spawnDuelistArc(this, baseX, baseY, arcPoints, dirX);
  }

  spawnTipperDetonation(x: number, y: number): void {
    _spawnTipperDetonation(this, x, y);
  }

  spawnDuelistStartup(x: number, y: number, angle: number): void {
    _spawnDuelistStartup(this, x, y, angle);
  }

  spawnDuelistDashStart(x: number, y: number, angle: number): void {
    _spawnDuelistDashStart(this, x, y, angle);
  }

  spawnDuelistAfterimage(x: number, y: number, angle: number, color: number): void {
    _spawnDuelistAfterimage(this, x, y, angle, color);
  }

  spawnDuelistSpeedLines(x: number, y: number, angle: number): void {
    _spawnDuelistSpeedLines(this, x, y, angle);
  }

  spawnDuelistBladeTrail(startX: number, startY: number, endX: number, endY: number, angle: number): void {
    _spawnDuelistBladeTrail(this, startX, startY, endX, endY, angle);
  }

  spawnDuelistSlashResolve(hitX: number, hitY: number, startX: number, startY: number, endX: number, endY: number, tier: number): void {
    _spawnDuelistSlashResolve(this, hitX, hitY, startX, startY, endX, endY, tier);
  }

  spawnBouncerBankShot(x: number, y: number, color: number, intensity: number): void {
    _spawnBouncerBankShot(this, x, y, color, intensity);
  }

  spawnScatterBuckshot(x: number, y: number, angle: number, color: number): void {
    _spawnScatterBuckshot(this, x, y, angle, color);
  }

  spawnVortexVoidTear(x: number, y: number, angle: number, color: number): void {
    _spawnVortexVoidTear(this, x, y, angle, color);
  }

  spawnGyroSpinLaunch(x: number, y: number, angle: number, color: number): void {
    _spawnGyroSpinLaunch(this, x, y, angle, color);
  }

  spawnCupidMarkApplied(x: number, y: number): void {
    _spawnCupidMarkApplied(this, x, y);
  }

  spawnCupidHeartbeatHit(x: number, y: number, isMarked: boolean, isBeat2: boolean): void {
    _spawnCupidHeartbeatHit(this, x, y, isMarked, isBeat2);
  }

  spawnCupidHeartbreak(x: number, y: number): void {
    _spawnCupidHeartbreak(this, x, y);
  }

  spawnFallingStarTrail(x: number, y: number, fallT: number, fallVy: number): void {
    _spawnFallingStarTrail(this, x, y, fallT, fallVy);
  }

  spawnFallingStarImpact(x: number, y: number, fallT: number): void {
    _spawnFallingStarImpact(this, x, y, fallT);
  }

  spawnTurnIndicator(x: number, y: number, color: number): void {
    _spawnTurnIndicator(this, x, y, color);
  }

  spawnAimSweepParticles(tipX: number, tipY: number, tangentAngle: number, playerColor: number, count: number, scale: number): void {
    _spawnAimSweepParticles(this, tipX, tipY, tangentAngle, playerColor, count, scale);
  }

  spawnKnockbackIndicator(px: number, py: number, vx: number, vy: number, color: number): void {
    _spawnKnockbackIndicator(this, px, py, vx, vy, color);
  }

  spawnBurstEntry(cx: number, cy: number, playerColor: number): void {
    _spawnBurstEntry(this, cx, cy, playerColor);
  }

  spawnBurstAmbient(cx: number, cy: number, playerColor: number): void {
    _spawnBurstAmbient(this, cx, cy, playerColor);
  }

  spawnBurstInputActive(cx: number, cy: number, inputX: number, inputY: number, playerColor: number): void {
    _spawnBurstInputActive(this, cx, cy, inputX, inputY, playerColor);
  }

  spawnBurstResolve(cx: number, cy: number, launchVx: number, launchVy: number, playerColor: number): void {
    _spawnBurstResolve(this, cx, cy, launchVx, launchVy, playerColor);
  }

  spawnBurstTrail(x: number, y: number, vx: number, vy: number, speed: number): void {
    _spawnBurstTrail(this, x, y, vx, vy, speed);
  }

  spawnControlSnap(cx: number, cy: number, playerColor: number): void {
    _spawnControlSnap(this, cx, cy, playerColor);
  }

  spawnRecoveryBurst(cx: number, cy: number, launchVx: number, launchVy: number, playerColor: number): void {
    _spawnRecoveryBurst(this, cx, cy, launchVx, launchVy, playerColor);
  }

  spawnBraceHeal(cx: number, cy: number, playerColor: number): void {
    _spawnBraceHeal(this, cx, cy, playerColor);
  }

  spawnEdgeguardEntry(cx: number, cy: number, playerColor: number): void {
    _spawnEdgeguardEntry(this, cx, cy, playerColor);
  }

  spawnEdgeguardFireLines(x: number, y: number, angle: number): void {
    _spawnEdgeguardFireLines(this, x, y, angle);
  }

  spawnEdgeguardEndFlash(x: number, y: number): void {
    _spawnEdgeguardEndFlash(this, x, y);
  }

  spawnDefenseEntry(cx: number, cy: number, playerColor: number): void {
    _spawnDefenseEntry(this, cx, cy, playerColor);
  }

  spawnBrakeExplosion(cx: number, cy: number, playerColor: number): void {
    _spawnBrakeExplosion(this, cx, cy, playerColor);
  }

  spawnBrakeWindowPulse(cx: number, cy: number, playerColor: number, progress: number): void {
    _spawnBrakeWindowPulse(this, cx, cy, playerColor, progress);
  }

  update(dt: number, frozen: boolean = false): void {
    // Fire pending delayed blasts (always — uses wall-clock time)
    const now = Date.now();
    for (let i = this.pendingBlasts.length - 1; i >= 0; i--) {
      if (now >= this.pendingBlasts[i].timestamp) {
        this.pendingBlasts[i].callback();
        this.pendingBlasts.splice(i, 1);
      }
    }

    // Update particles — freeze-immune particles keep animating during hitstop
    const dtScale = dt * 60; // normalize to 60fps reference rate

    // Bolt: Clear cache every frame to avoid stale entries, but reuse Map object allocation
    this.frictionCache.clear();

    for (let i = 0; i < this.activeCount; i++) {
      const p = this.pool[i];
      if (frozen && !p.freezeImmune) continue; // frozen non-immune: skip

      p.life -= dt;
      if (p.life <= 0) {
        p.active = false;
        p.sprite.visible = false;

        // Swap-remove to keep active list contiguous
        this.activeCount--;
        const last = this.pool[this.activeCount];
        this.pool[this.activeCount] = p;
        this.pool[i] = last;
        i--; // Re-process the swapped particle (which is now at i)
        continue;
      }

      // Physics (dt-scaled, normalized to 60fps)
      p.vy += p.gravity * dtScale;
      p.x += p.vx * dtScale;
      p.y += p.vy * dtScale;
      let ff = this.frictionCache.get(p.friction);
      if (ff === undefined) {
        ff = Math.pow(p.friction, dtScale);
        this.frictionCache.set(p.friction, ff);
      }
      p.vx *= ff;
      p.vy *= ff;

      // Non-linear fade: hold bright/big, then drop fast at end of life
      const t = p.life / p.maxLife;
      const alphaCurve = Math.min(1, t * 3);    // holds at 1.0 for first 67% of life, then drops
      const scaleCurve = Math.min(1, t * 2);    // holds at 1.0 for first 50% of life, then shrinks
      p.sprite.position.set(p.x, p.y);
      p.sprite.alpha = p.startAlpha * alphaCurve;

      if (p.stretch > 1) {
        // Velocity-aligned: rotate to face travel direction, stretch along it
        p.sprite.rotation = Math.atan2(p.vy, p.vx);
        p.sprite.scale.set(p.startScale * p.stretch * scaleCurve, p.startScale * scaleCurve);
      } else {
        p.sprite.rotation += p.rotationSpeed * dt;
        p.sprite.scale.set(p.startScale * scaleCurve);
      }
    }

    // Update shockwaves — freeze-immune ones animate during hitstop, others hold
    this.shockwaveGfx.clear();

    for (let i = this.shockwaves.length - 1; i >= 0; i--) {
      const sw = this.shockwaves[i];
      const animate = !frozen || sw.freezeImmune;

      if (animate) {
        sw.life -= dt;
        sw.radius += sw.expandSpeed * dt;
      }

      if (sw.life <= 0 || sw.radius >= sw.maxRadius) {
        this.shockwaves.splice(i, 1);
        continue;
      }

      const t = sw.life / sw.maxLife;
      this.shockwaveGfx.circle(sw.x, sw.y, sw.radius);
      this.shockwaveGfx.stroke({
        color: sw.color,
        width: sw.lineWidth * t,
        alpha: t * 0.8,
      });
    }

    // Update impact lines — freeze-immune ones (hit cross, knockback) animate during hitstop
    for (let i = this.impactLines.length - 1; i >= 0; i--) {
      const il = this.impactLines[i];
      const animate = !frozen || il.freezeImmune;

      if (animate) {
        il.life -= dt;
        il.innerRadius += il.expandSpeed * dt * 0.6;
        il.outerRadius += il.expandSpeed * dt;
      }

      if (il.life <= 0) {
        this.impactLines.splice(i, 1);
        continue;
      }

      const t = il.life / il.maxLife;
      this.shockwaveGfx.moveTo(
        il.x + Math.cos(il.angle) * il.innerRadius,
        il.y + Math.sin(il.angle) * il.innerRadius,
      );
      this.shockwaveGfx.lineTo(
        il.x + Math.cos(il.angle) * il.outerRadius,
        il.y + Math.sin(il.angle) * il.outerRadius,
      );
      this.shockwaveGfx.stroke({
        color: il.color,
        width: il.lineWidth * t,
        alpha: t,
      });
    }

    // Update beam lines — 3-pass glow (outer, mid, white core)
    for (let i = this.beamLines.length - 1; i >= 0; i--) {
      const bl = this.beamLines[i];
      const animate = !frozen || bl.freezeImmune;

      if (animate) {
        bl.life -= dt;
      }

      if (bl.life <= 0) {
        this.beamLines.splice(i, 1);
        continue;
      }

      const t = bl.life / bl.maxLife;
      const alpha = Math.min(1, t * 3); // hold bright, fade at end

      // Pass 1: outer glow
      this.shockwaveGfx.moveTo(bl.x, bl.startY);
      this.shockwaveGfx.lineTo(bl.x, bl.endY);
      this.shockwaveGfx.stroke({ color: bl.color, width: bl.width * 3, alpha: alpha * 0.2 });

      // Pass 2: mid glow
      this.shockwaveGfx.moveTo(bl.x, bl.startY);
      this.shockwaveGfx.lineTo(bl.x, bl.endY);
      this.shockwaveGfx.stroke({ color: bl.color, width: bl.width * 1.5, alpha: alpha * 0.5 });

      // Pass 3: white core
      this.shockwaveGfx.moveTo(bl.x, bl.startY);
      this.shockwaveGfx.lineTo(bl.x, bl.endY);
      this.shockwaveGfx.stroke({ color: 0xffffff, width: bl.width * 0.5, alpha: alpha * 0.9 });
    }
  }

  destroy(): void {
    this.container.destroy({ children: true });
    this.hardGlowTexture.destroy(true);
    this.softGlowTexture.destroy(true);
    this.clusterTexture.destroy(true);
  }
}
